import { zodResolver } from '@hookform/resolvers/zod';
import {
  mfaVerifyRequestSchema,
  type MfaEnrollResponse,
  type MfaVerifyRequest,
} from '@homecare/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { Button, Card, EmptyState, Field, PageHeader } from '@homecare/ui';

import { ErrorNote } from '../../components/primitives';
import { request } from '../../lib/api';
import { useSession } from '../../lib/session';

/**
 * Set up an authenticator (§7).
 *
 * Two steps in one screen, because they are one task: generate the secret, then
 * prove the phone has it. The secret is shown as a grouped monospace string
 * rather than only as a QR code — a scheduler on a desktop with the
 * authenticator on a locked phone often has to type it, and hiding it behind a
 * camera is a support call.
 *
 * ## Why this one wears console chrome and the other four wear the auth card
 *
 * It is reached from the user menu, by someone already signed in, at
 * `/account/two-step`. It is an account setting rather than a step in getting
 * through the door, so it sits inside the shell on a white card with a
 * `PageHeader`, exactly like every other console screen.
 *
 * The components are the same ones the auth screens use — same `Field`, same
 * `Button`, same card surface, border, radius and elevation — so the set still
 * reads as one designer's work. What changes is the frame around them, and it
 * changes because the context genuinely has: there is a rail and a header here,
 * and pretending otherwise would mean a signed-in person landing on a screen
 * that looks like it signed them out.
 */
export function MfaEnrolment(): ReactNode {
  const accessToken = useSession((state) => state.accessToken);
  const restore = useSession((state) => state.restore);
  const enrolled = useSession((state) => state.user?.mfaEnrolled ?? false);

  const [secret, setSecret] = useState<MfaEnrollResponse | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [starting, setStarting] = useState(false);

  const form = useForm<MfaVerifyRequest>({
    resolver: zodResolver(mfaVerifyRequestSchema),
    defaultValues: { code: '' },
    mode: 'onBlur',
  });

  useEffect(() => {
    if (form.formState.isSubmitSuccessful) void restore();
  }, [form.formState.isSubmitSuccessful, restore]);

  async function start(): Promise<void> {
    setFailure(null);
    setStarting(true);
    try {
      setSecret(
        await request<MfaEnrollResponse>('/v1/auth/mfa/enroll', { method: 'POST', accessToken }),
      );
    } catch (error) {
      setFailure(error);
    } finally {
      setStarting(false);
    }
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setFailure(null);
    try {
      await request('/v1/auth/mfa/verify', { method: 'POST', accessToken, body: values });
    } catch (error) {
      setFailure(error);
    }
  });

  if (enrolled) {
    return (
      <div className="grid max-w-2xl gap-6">
        <PageHeader title="Two-step sign-in" meta="Set up on this account" />
        <Card>
          <p className="text-md text-ink">Your authenticator app is set up.</p>
          <p className="text-ink-3 mt-2 text-sm">
            You'll be asked for a code from it each time you sign in. To use a different phone, ask
            an administrator at your agency to remove this one first.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid max-w-2xl gap-6">
      <PageHeader
        title="Two-step sign-in"
        meta="Owners, administrators and billers sign in with a code from an authenticator app as well as a password."
      />

      <div className="grid gap-6">
        <ErrorNote error={failure} nextStep="Try again, or ask your administrator for help." />

        {!secret ? (
          <Card padded={false}>
            <EmptyState
              title="No authenticator yet"
              explanation="Set one up with Google Authenticator, 1Password, Authy — any app that generates 6-digit codes. It takes about a minute."
              action={
                <Button pending={starting} onClick={() => void start()}>
                  Set up authenticator
                </Button>
              }
            />
          </Card>
        ) : form.formState.isSubmitSuccessful ? (
          <Card>
            <p className="text-md text-ink">Your authenticator app is set up.</p>
            <p className="text-ink-3 mt-2 text-sm">
              You'll be asked for a code from it each time you sign in.
            </p>
          </Card>
        ) : (
          <>
            <Card>
              <p className="eyebrow">Step 1 — add the key</p>
              <p className="text-ink-2 mt-2 text-sm">
                Scan the code in your authenticator app, or type this key into it.
              </p>
              <p
                className="bg-surface-2 text-md text-ink mt-3 select-all break-all rounded-md px-3 py-2.5 font-mono"
                data-testid="mfa-secret"
              >
                {secret.secret.match(/.{1,4}/g)?.join(' ')}
              </p>
            </Card>

            <Card>
              <form noValidate onSubmit={onSubmit} className="grid gap-6">
                <p className="eyebrow">Step 2 — confirm it works</p>

                <Field
                  label="6-digit code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  hint="Enter the code showing in your app right now."
                  error={form.formState.errors.code?.message}
                  {...form.register('code')}
                />

                <div>
                  <Button type="submit" pending={form.formState.isSubmitting}>
                    Confirm code
                  </Button>
                </div>
              </form>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
